import { createModel } from '@rematch/core';
import { FilterOperatorEnum, Paged } from 'domains/appDomain';
import { EquipmentsAvailablePress } from 'domains/eqptDomain';
import {
  InkFinishes,
  ListSkuParam,
  MultiTypeEnum,
  PartialSKU,
  PlatformSkuPaperOptions,
  SKU,
  SKUPart,
  SkuAddFilesPayload,
  SkuFilterObj,
  SkuGalleryLabelsAll,
  SkuPaperOptions,
  SkuSizePairPayload,
  SkuType,
  StepActionItem,
  StepActionPayload,
  UpdatePaper,
  UpdateSize,
  UpdateSkuPartParam,
} from 'domains/skuDomain';
import type { RootModel } from 'models';
import { BaseState, baseReducers } from 'models/baseReducers';
import jobconfigService from 'services/jobconfigService';
import skuService from 'services/skuService';
import {
  JobSpec,
  PlatformSkuPaperUpdateParams,
} from './../domains/jobconfigDomain';
import { toast } from '../components/RevxMessage';

export type PagerState = {
  pageSize: number;
  currentPage: number;
};

export type PagerStateProp =
  | 'defaultPager'
  | 'inheritBasePager'
  | 'inheritParentPager'
  | 'inheritCurrentPager';
interface SkuState extends BaseState {
  pagedData?: Paged<SKU>;
  defaultPager: PagerState;
  inheritBasePager: PagerState;
  inheritParentPager: PagerState;
  inheritCurrentPager: PagerState;
  pagerStateProp?: PagerStateProp;
  curItem?: PartialSKU | SKUPart;
  curTopItem?: PartialSKU;
  filterObj: SkuFilterObj;
  skuTypes: SkuType[];
  createWithInheritanceVisible: boolean;
  needSaveDraft: boolean;
  skuTesterState: SkuTesterState;
  stepAction?: StepActionItem;
  stepActionPayload?: StepActionPayload;
  singleStep: number;
  multiStep: number;
  revxSpinloading: boolean;
}

export const SKU_NOT_DRAFT_FILTER = {
  operator: FilterOperatorEnum.EQUALS,
  values: [false],
};

type SkuTesterState = {
  visible: boolean;
  curItem?: SKU;
  jobSpec?: JobSpec;
};

export const sku = createModel<RootModel>()({
  state: {
    pagedData: undefined,
    pagerStateProp: 'defaultPager',
    defaultPager: {
      pageSize: 20,
      currentPage: 1,
    },
    inheritBasePager: {
      pageSize: 20,
      currentPage: 1,
    },
    inheritParentPager: {
      pageSize: 20,
      currentPage: 1,
    },
    inheritCurrentPager: {
      pageSize: 20,
      currentPage: 1,
    },
    curItem: undefined,
    curTopItem: undefined,
    needSaveDraft: false,
    skuTypes: [],
    filterObj: {
      draft: SKU_NOT_DRAFT_FILTER,
    },
    createWithInheritanceVisible: false,
    skuTesterState: {
      visible: false,
      curItem: undefined,
      jobSpec: undefined,
    },
    stepAction: {},
    stepActionPayload: {},
    singleStep: 0,
    multiStep: 0,
    revxSpinloading: false,
  } as SkuState,
  reducers: {
    ...baseReducers<SkuState>(),
    reset: (state: SkuState) => {
      state.pagedData = undefined;
      state.filterObj = {};
      state.skuTesterState = {
        visible: false,
        curItem: undefined,
        jobSpec: undefined,
      };
      state.curItem = undefined;
      state.curTopItem = undefined;
      state.createWithInheritanceVisible = false;
      state.defaultPager = {
        pageSize: 20,
        currentPage: 1,
      };
      return state;
    },

    resetInheritTablePager: (state: SkuState) => {
      state.inheritBasePager = {
        pageSize: 20,
        currentPage: 1,
      };
      state.inheritParentPager = {
        pageSize: 20,
        currentPage: 1,
      };
      state.inheritCurrentPager = {
        pageSize: 20,
        currentPage: 1,
      };
      state.createWithInheritanceVisible = false;
      state.pagerStateProp = 'defaultPager';

      return state;
    },

    setSkuTesterVisible: (state: SkuState, skuTesterVisible: boolean) => {
      state.skuTesterState.visible = skuTesterVisible;
      return state;
    },
    setSkuTesterItem: (state: SkuState, item: SKU) => {
      state.skuTesterState.curItem = item;
      return state;
    },
    setSkuTesterJobSpec: (state: SkuState, jobSpec: JobSpec) => {
      state.skuTesterState.jobSpec = jobSpec;
      return state;
    },
    resetSkuTester: (state: SkuState) => {
      state.skuTesterState = {
        visible: false,
        curItem: undefined,
        jobSpec: undefined,
      };
      return state;
    },
  },
  effects: (dispatch) => {
    const { sku } = dispatch;
    return {
      async getSKUs(payload: ListSkuParam): Promise<void> {
        const pagedData = await skuService.getPagedSkus(payload);
        sku.setState({
          pagedData: pagedData,
        });
      },

      async getSkuTypes(): Promise<void> {
        const resp = await skuService.getSkuTypes();
        sku.setState({
          skuTypes: resp,
        });
      },

      async updateCurItemForLaterSave(
        payload: PartialSKU,
        state
      ): Promise<void> {
        sku.updateCurItemHelper({
          sku: payload,
          needSaveDraft: true,
        });
      },

      async updateCurItemForSave(payload: PartialSKU): Promise<void> {
        try {
          await sku.saveSku(payload);
        } catch {}
      },

      async saveSku(payload: PartialSKU, state): Promise<void> {
        if (payload.id) {
          const prevItem = state.sku.curItem;
          try {
            sku.updateCurItemHelper({
              sku: payload,
            });
            const updatedSKUDraft = await skuService.updateSkuSummary(payload);
            sku.updateCurItemHelper({
              sku: updatedSKUDraft,
              needSaveDraft: false,
            });
          } catch (e) {
            sku.setState({
              curItem: prevItem,
            });
            toast.error(e.originalMessage);
          }
        } else {
          const draftSku = await skuService.createSku({
            isMulti: Boolean(payload.multiType === MultiTypeEnum.WRAPPER),
            inheritFromId: payload.inheritedFromId,
          });

          sku.setState({
            curItem: draftSku,
          });
        }
      },

      async saveSkuWithInheritance(payload: PartialSKU, state): Promise<void> {
        const { id } = await skuService.createSku({
          isMulti: Boolean(payload.multiType === MultiTypeEnum.WRAPPER),
          inheritFromId: payload.inheritedFromId,
        });
        const draftSku = await skuService.startEditSku({ id });
        sku.setState({
          curItem: draftSku,
        });
      },

      async refresh(payload, state) {
        sku.setFilter({
          ...state.sku.filterObj,
        });
      },

      async removeSku(payload: PartialSKU, state): Promise<void> {
        await skuService.deleteSku(payload);

        await sku.refresh();
      },

      async updateSkuActiveStatus(payload: {
        skuId: number;
        activeStatus: boolean;
      }): Promise<void> {
        await skuService.updateSkuActiveStatus(
          payload.skuId,
          payload.activeStatus
        );

        await sku.refresh();
      },

      async saveFilter(payload: SkuFilterObj, state): Promise<void> {
        const pagerStateProp = state.sku.pagerStateProp;
        const curPager = state.sku[pagerStateProp || 'defaultPager'];

        sku.setState({
          filterObj: payload,
          [pagerStateProp || 'defaultPager']: {
            pageSize: curPager.pageSize,
            currentPage: 1,
          },
        });
      },

      async removeFilter(payload: void, state): Promise<void> {
        const pagerStateProp = state.sku.pagerStateProp;
        const curPager = state.sku[pagerStateProp || 'defaultPager'];

        sku.setState({
          filterObj: {},
          [pagerStateProp || 'defaultPager']: {
            pageSize: curPager.pageSize,
            currentPage: 1,
          },
        });
      },

      setDefaultPage(payload: PagerState, state): void {
        sku.setState({
          defaultPager: payload,
        });
      },

      async getJobSpec(payload: number): Promise<void> {
        const jobSpec = await jobconfigService.getJobSpec(payload);
        sku.setSkuTesterJobSpec(jobSpec);
      },

      async getPaperOptions(): Promise<PlatformSkuPaperOptions> {
        return skuService.getPaperOptions();
      },

      async getPaperCount(paperOption: SkuPaperOptions): Promise<number> {
        return skuService.getPaperCount(paperOption);
      },

      async updateSize(payload: Partial<UpdateSize>, state): Promise<Boolean> {
        const prevItem = state.sku.curItem;
        try {
          const updatedSKUDraft = await skuService.updateSize(payload);
          sku.updateCurItemHelper({ sku: updatedSKUDraft });
          return Boolean(updatedSKUDraft);
        } catch (e) {
          console.log(e);
          sku.setState({
            curItem: prevItem,
          });
          return false;
        }
      },

      async platformUpdatePaperHelper(
        payload: PlatformSkuPaperUpdateParams,
        state
      ): Promise<Boolean> {
        const prevItem = state.sku.curItem;
        try {
          const skuDraft = await skuService.platformUpdatePaper(
            payload.id,
            payload
          );
          sku.updateCurItemHelper({ sku: skuDraft });
          return Boolean(skuDraft);
        } catch {
          sku.setState({
            curItem: prevItem,
          });
          return false;
        }
      },

      async updateCurItemHelper(
        payload: { sku: PartialSKU; needSaveDraft?: boolean },
        state
      ) {
        const topItem = state.sku.curTopItem;
        const prevItem: SKUPart = state.sku.curItem as SKUPart;
        if (topItem) {
          if (topItem.id === payload.sku.id) {
            const payloadPart = payload.sku?.parts?.find(
              (item) => item?.id === prevItem?.id
            );
            sku.setState({
              curTopItem: {
                ...payload.sku,
              },
              curItem: payloadPart,
              needSaveDraft:
                payload.needSaveDraft === undefined
                  ? state.sku.needSaveDraft
                  : payload.needSaveDraft,
            });
          } else if (payload.sku.id === prevItem.part.id) {
            const newItem: SKUPart = {
              ...prevItem,
              part: {
                ...prevItem.part,
                ...payload.sku,
              },
            };
            sku.setState({
              curItem: newItem,
              needSaveDraft:
                payload.needSaveDraft === undefined
                  ? state.sku.needSaveDraft
                  : payload.needSaveDraft,
            });
          }
        } else {
          const prevItem: SKU = state.sku.curItem as SKU;
          sku.setState({
            curItem: {
              ...prevItem,
              ...payload.sku,
            },
            needSaveDraft:
              payload.needSaveDraft === undefined
                ? false
                : payload.needSaveDraft,
          });
        }
      },

      async updateQuantities(payload: PartialSKU, state): Promise<Boolean> {
        const prevItem = state.sku.curItem;
        try {
          const updatedSKUDraft = await skuService.updateQuantities(payload);
          sku.updateCurItemHelper({ sku: updatedSKUDraft });

          return Boolean(updatedSKUDraft);
        } catch (e) {
          console.log(e);
          sku.setState({
            curItem: prevItem,
          });
          return false;
        }
      },

      registerStepAction(action: StepActionItem, state): void {
        const prevStepAction = state.sku.stepAction;

        sku.setState({
          stepAction: {
            ...prevStepAction,
            ...action,
          },
        });
      },

      registerStepActionPayload(actionPayload: StepActionPayload, state): void {
        const prevStepActionPayload = state.sku.stepActionPayload;

        sku.setState({
          stepActionPayload: {
            ...prevStepActionPayload,
            ...actionPayload,
          },
        });
      },

      setSingleStep(step: number): void {
        sku.setState({
          singleStep: step,
        });
      },

      setMultiStep(step: number): void {
        sku.setState({
          multiStep: step,
        });
      },

      setRevxSpinloading(loading: boolean): void {
        sku.setState({
          revxSpinloading: loading,
        });
      },
      showSkuLoading(): void {
        sku.setState({
          revxSpinloading: true,
        });
      },
      hideSkuLoading(): void {
        sku.setState({
          revxSpinloading: false,
        });
      },

      async updatePaper(payload: UpdatePaper, state): Promise<any> {
        const prevItem = state.sku.curItem;
        try {
          const updatedSKUDraft = await skuService.updatePaper(payload);
          sku.updateCurItemHelper({ sku: updatedSKUDraft });
          return Boolean(updatedSKUDraft);
        } catch (e) {
          console.log(e);
          sku.setState({
            curItem: prevItem,
          });
          return false;
        }
      },

      async getInk(): Promise<InkFinishes> {
        return skuService.getInk();
      },

      async getGalleryLabels(): Promise<SkuGalleryLabelsAll> {
        return skuService.getGalleryLabels();
      },

      async updateInk(payload: PartialSKU, state): Promise<Boolean> {
        const prevItem = state.sku.curItem;
        try {
          const updatedSKUDraft = await skuService.updateInk(payload);
          sku.updateCurItemHelper({ sku: updatedSKUDraft });
          return Boolean(updatedSKUDraft);
        } catch (e) {
          console.log(e);
          sku.setState({
            curItem: prevItem,
          });
          return false;
        }
      },

      async getPresses(id: number): Promise<EquipmentsAvailablePress> {
        return skuService.getPresses(id);
      },

      async updatePresses(payload: PartialSKU, state): Promise<Boolean> {
        const prevItem = state.sku.curItem;
        try {
          const updatedSKUDraft = await skuService.updatePresses(payload);
          sku.updateCurItemHelper({ sku: updatedSKUDraft });
          return Boolean(updatedSKUDraft);
        } catch (e) {
          console.log(e);
          sku.setState({
            curItem: prevItem,
          });
          return false;
        }
      },

      async updateSvc(payload: PartialSKU, state): Promise<Boolean> {
        const prevItem = state.sku.curItem;
        try {
          const updatedSKUDraft = await skuService.updateSvc(payload);
          sku.updateCurItemHelper({ sku: updatedSKUDraft });
          return Boolean(updatedSKUDraft);
        } catch (e) {
          console.log(e);
          sku.setState({
            curItem: prevItem,
          });
          return false;
        }
      },

      async updateExtraSpecs(payload: PartialSKU, state): Promise<Boolean> {
        const prevItem = state.sku.curItem;
        try {
          const updatedSKUDraft = await skuService.updateExtraSpecs(payload);
          sku.updateCurItemHelper({ sku: updatedSKUDraft });
          return Boolean(updatedSKUDraft);
        } catch (e) {
          console.log(e);
          sku.setState({
            curItem: prevItem,
          });
          return false;
        }
      },

      async updateSkuPartQuantities(
        payload: UpdateSkuPartParam,
        state
      ): Promise<Boolean> {
        const prevItem = state.sku.curItem;
        try {
          const updatedSKUDraft = await skuService.updateSkuPartQuantities(
            payload
          );
          sku.updateCurItemHelper({ sku: updatedSKUDraft });
          return Boolean(updatedSKUDraft);
        } catch (e) {
          console.log(e);
          sku.setState({
            curItem: prevItem,
          });
          return false;
        }
      },

      async updateSkuPartSizeBatch(
        payload: Partial<SkuSizePairPayload>,
        state
      ): Promise<Boolean> {
        const prevItem = state.sku.curItem;
        try {
          const updatedSKUDraft = await skuService.updateSkuPartSizeBatch(
            payload
          );
          sku.updateCurItemHelper({ sku: updatedSKUDraft });
          return Boolean(updatedSKUDraft);
        } catch (e) {
          console.log(e);
          sku.setState({
            curItem: prevItem,
          });
          return false;
        }
      },
      async startViewSku(id: number): Promise<SKU> {
        const skuDetail = await skuService.startViewSku(
          {
            id: id,
          },
          true
        );
        return skuDetail;
      },

      // async updateSkuSvc(payload: { param: SvcGroup, showType: SvcShowTypeEnum, id?: number }, state): Promise<Boolean> {
      //   const prevItem = state.sku.curItem;
      //   try {
      //     const updatedSKUDraft = await skuService.updateSkuSvc(payload);
      //     sku.updateCurItemHelper({ sku: updatedSKUDraft });
      //     return Boolean(updatedSKUDraft);
      //   } catch {
      //     sku.setState({
      //       curItem: prevItem,
      //     });
      //     return false;
      //   }
      // },

      async getOptionSvcs(payload: { id?: number }): Promise<any> {
        return await skuService.getOptionSvcs(payload);
      },

      async skuAddFiles(
        payload: Partial<SkuAddFilesPayload>,
        state
      ): Promise<any> {
        const prevItem = state.sku.curItem;
        try {
          const updatedSKUDraft = await skuService.skuAddFiles(payload);
          sku.updateCurItemHelper({ sku: updatedSKUDraft });
          return Boolean(updatedSKUDraft);
        } catch (e) {
          console.log(e);
          sku.setState({
            curItem: prevItem,
          });
          return false;
        }
      },
    };
  },
});
